import React from 'react'
import { useAuth } from '../contexts/AuthContext'
import { Chrome, Download, ArrowRight } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

export default function Login() {
  const { signIn } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState(null)

  const handleSignIn = async () => {
    setLoading(true)
    setError(null)
    
    const { error } = await signIn()
    
    if (error) {
      setError(error)
      setLoading(false)
    }
  }

  const handleDownloadClick = () => {
    navigate('/setup')
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-500 to-purple-500 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full">
        {/* Logo & Branding */}
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">🧠</div>
          <h1 className="text-4xl font-bold text-white mb-2">Cognify</h1>
          <p className="text-white/90 text-lg">AI Interview Mentor Dashboard</p>
        </div>

        {/* Download Extension CTA - PROMINENT */}
        <div className="bg-white rounded-2xl shadow-2xl p-8 mb-6 border-4 border-yellow-400">
          <div className="text-center mb-4">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-500 to-purple-500 rounded-full mb-4">
              <Download className="text-white" size={32} />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">🚀 First Time Here?</h2>
            <p className="text-gray-600 text-lg mb-6">Download and install the Cognify Chrome Extension to get started!</p>
          </div>
          
          <button
            onClick={handleDownloadClick}
            className="w-full bg-gradient-to-r from-primary-500 to-purple-500 text-white font-bold py-4 px-6 rounded-xl flex items-center justify-center space-x-3 hover:from-primary-600 hover:to-purple-600 transition-all duration-200 transform hover:scale-105 shadow-lg text-lg"
          >
            <Download size={24} />
            <span>Download Extension & Setup Guide</span>
            <ArrowRight size={24} />
          </button>
          
          <p className="text-center text-sm text-gray-500 mt-4">
            Takes less than 2 minutes to install • Works with LeetCode, Codeforces & more
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Welcome Back</h2>
            <p className="text-gray-600">Sign in to view your progress and insights</p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {error}
            </div>
          )}

          <button
            onClick={handleSignIn}
            disabled={loading}
            className="w-full bg-white border-2 border-gray-300 hover:border-primary-500 text-gray-700 font-semibold py-3 px-4 rounded-lg flex items-center justify-center space-x-3 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            <span>{loading ? 'Signing in...' : 'Sign in with Google'}</span>
          </button>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-start space-x-2 text-sm text-gray-600">
              <Chrome size={20} className="text-primary-500 flex-shrink-0 mt-0.5" />
              <p>
                Already have the extension? Sign in to view your progress and insights.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-white/80 text-sm">
          <p>Think better. Code smarter. Interview with confidence.</p>
        </div>
      </div>
    </div>
  )
}
